import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { PHOTOS } from "../lib/assets";
import { useEnquiry } from "../context/EnquiryContext";

const MODULAR = [
  { name: "Modular Kitchens", img: PHOTOS.kitchen2, desc: "Ergonomic, built-to-fit kitchens engineered for Indian homes.", href: "/modular-kitchens" },
  { name: "Wardrobes", img: PHOTOS.wardrobe, desc: "Sliding, hinged & walk-in wardrobes with smart internals.", href: "/wardrobes" },
  { name: "Storage", img: PHOTOS.living2, desc: "Considered storage that disappears into the architecture.", href: "/storage" },
];

const CIVIL = [
  { name: "Flooring", img: PHOTOS.flooring },
  { name: "Painting", img: PHOTOS.living3 },
  { name: "False Ceiling", img: PHOTOS.bedroom2 },
  { name: "Wall Decor", img: PHOTOS.walldecor },
];

export default function Services() {
  const { openEnquiry } = useEnquiry();
  return (
    <section id="services" className="py-24 md:py-32 bg-warm" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <Reveal><Overline>What we do</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight text-navy mt-5 leading-[1.02]">
                One studio for<br />every surface of home.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-ink/70 md:max-w-xs md:text-right leading-relaxed">
              From modular joinery to civil finishing — designed, installed and delivered by a single accountable team.
            </p>
          </Reveal>
        </div>

        {/* Modular — large editorial cards */}
        <div className="mb-8">
          <Reveal><p className="text-xs tracking-[0.25em] uppercase text-navy/40 mb-6">01 — Modular Interiors</p></Reveal>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {MODULAR.map((s, i) => {
              const card = (
                <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="group relative overflow-hidden border border-navy/10 bg-white h-full" data-testid={`service-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="overflow-hidden aspect-[4/5]">
                    <img src={s.img} alt={`${s.name} — DIDIT modular interiors in Delhi NCR`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-2xl font-normal text-navy">{s.name}</h3>
                      <ArrowUpRight size={20} className="text-navy/40 group-hover:text-terracotta group-hover:rotate-45 transition-all duration-300" />
                    </div>
                    <p className="text-ink/65 text-sm mt-2 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
              return (
                <Reveal key={s.name} delay={i * 0.08}>
                  {s.href ? (
                    <Link to={s.href} className="block h-full" data-testid={`service-link-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      {card}
                    </Link>
                  ) : card}
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Civil / finishing — compact editorial band */}
        <div className="mt-14">
          <Reveal><p className="text-xs tracking-[0.25em] uppercase text-navy/40 mb-6">02 — Civil / Finishing Services</p></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {CIVIL.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.06}>
                <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="group relative overflow-hidden aspect-square border border-navy/10" data-testid={`service-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <img src={s.img} alt={`${s.name} — DIDIT interior finishing service`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <h3 className="font-display text-xl text-warm font-normal">{s.name}</h3>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex justify-center">
            <button onClick={() => openEnquiry("quote", "services")} data-testid="services-quote-btn"
              className="rounded-full border border-navy text-navy px-8 py-3.5 text-sm font-medium hover:bg-navy hover:text-warm transition-colors">
              Estimate my project
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
